import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  PhoneCall, MessageSquare, Loader2, PhoneIncoming, PhoneOutgoing,
  ArrowUpRight, ArrowDownLeft, Play, Smartphone,
} from "lucide-react";
import NativePhoneDialog from "@/components/NativePhoneDialog";

const SMS_TEMPLATES = [
  {
    key: "intro",
    label: "Intro",
    body:
      "Hi {name}, this is {agent} with FlipPilot. I saw your property at " +
      "{address} and wanted to see if you'd consider a fair cash offer. " +
      "Reply STOP to opt out.",
  },
  {
    key: "follow_up",
    label: "Follow up",
    body:
      "Hi {name}, just following up on the offer for {address}. Any " +
      "questions I can answer? — {agent}",
  },
  {
    key: "voicemail",
    label: "Missed call",
    body:
      "Hi {name}, tried to reach you about your property at {address}. " +
      "Call me back when you get a chance. — {agent}",
  },
];

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function TimelineRow({ row }) {
  const isCall = row.kind === "call";
  const isOut = row.direction === "outbound";
  const Icon = isCall
    ? (isOut ? PhoneOutgoing : PhoneIncoming)
    : (isOut ? ArrowUpRight : ArrowDownLeft);
  return (
    <div
      className="flex gap-3 py-2 border-b border-[var(--app-line)] last:border-b-0"
      data-testid={`hist-${row.kind}-${row.id}`}
    >
      <div className={`shrink-0 mt-0.5 ${
        isOut ? "text-[var(--app-accent)]" : "text-emerald-600"
      }`}>
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[var(--app-text-muted)]">
          <span>{isCall ? "Call" : "SMS"}</span>
          <span>·</span>
          <span>{isOut ? "Outbound" : "Inbound"}</span>
          {row.status && <span>· {row.status}</span>}
          <span className="ml-auto font-normal normal-case tracking-normal">
            {fmtDate(row.created_at)}
          </span>
        </div>
        {isCall ? (
          <div className="text-xs mt-1 text-[var(--app-text)]">
            <span className="font-mono">
              {isOut ? row.to_number : row.from_number}
            </span>
            {row.duration && (
              <span className="text-[var(--app-text-muted)] ml-2">
                · {row.duration}s
              </span>
            )}
            {row.recording_url && (
              <div className="mt-1.5">
                <audio
                  controls
                  src={`${row.recording_url}.mp3`}
                  className="h-8 w-full max-w-sm"
                  data-testid={`rec-${row.id}`}
                >
                  <a href={`${row.recording_url}.mp3`} target="_blank" rel="noreferrer">
                    <Play size={12} /> Recording
                  </a>
                </audio>
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm mt-1 whitespace-pre-wrap break-words">
            {row.body}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Reusable Call/SMS dialog.
 * Props:
 *  - open, onOpenChange
 *  - phone (E.164 or raw; the seller/target number)
 *  - name (display only)
 *  - address (used for template merge)
 *  - leadId (optional; associates activity to a CRM lead)
 *  - defaultTab: 'call' | 'sms' | 'history'
 */
export default function CallSmsDialog({
  open, onOpenChange,
  phone = "",
  name = "",
  address = "",
  leadId = null,
  defaultTab = "call",
}) {
  const nav = useNavigate();
  const [tab, setTab] = useState(defaultTab);
  const [config, setConfig] = useState({ configured: false, agent_phone: "", from_number: "" });
  const [agentPhone, setAgentPhone] = useState("");
  const [savingAgent, setSavingAgent] = useState(false);
  const [smsBody, setSmsBody] = useState("");
  const [smsBusy, setSmsBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [me, setMe] = useState(null);
  const [nativeDialog, setNativeDialog] = useState({ open: false, mode: "call" });

  useEffect(() => { if (open) setTab(defaultTab); }, [open, defaultTab]);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/twilio/config");
      setConfig(data);
      setAgentPhone(data.agent_phone || "");
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/auth/me");
      setMe(data);
    } catch { /* ignore */ }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const params = leadId ? { lead_id: leadId } : { phone };
      const { data } = await api.get("/twilio/history", { params });
      setHistory(data || []);
    } catch { setHistory([]); }
    finally { setHistLoading(false); }
  }, [leadId, phone]);

  useEffect(() => {
    if (!open) return;
    loadConfig();
    loadHistory();
  }, [open, loadConfig, loadHistory]);

  const saveAgentPhone = async () => {
    if (!agentPhone.trim()) return;
    setSavingAgent(true);
    try {
      const { data } = await api.put("/twilio/agent-phone", {
        agent_phone: agentPhone.trim(),
      });
      setConfig((c) => ({ ...c, agent_phone: data.agent_phone }));
      setAgentPhone(data.agent_phone);
      toast.success("Cell number saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingAgent(false); }
  };

  // Route the "Call" button through the SignalWire SIP dialer (Dialer.jsx
  // + signalwireSoftphone.js), same path the Dialer / AI Lead Manager /
  // Lead Drawer already use. Stashes seller context in localStorage so
  // the customer card renders immediately while the SIP softphone
  // registers, then Dialer auto-fires the call once Ready.
  const placeCall = () => {
    if (!phone) return toast.error("No phone number to call");
    try {
      localStorage.setItem("dialer.pendingCall", JSON.stringify({
        lead_id:          leadId || null,
        phone,
        seller_name:      name || null,
        property_address: address || null,
        source:           "call_sms_dialog",
        at:               Date.now(),
      }));
    } catch { /* localStorage may be blocked — Dialer falls back to query params */ }
    const params = new URLSearchParams();
    if (leadId) params.set("leadId", leadId);
    else {
      // No CRM lead → synthetic dial. Pass phone + optional context on the query.
      params.set("phone", phone);
      if (name)    params.set("name", name);
      if (address) params.set("address", address);
    }
    onOpenChange?.(false);
    nav(`/dialer?${params.toString()}`);
  };

  const applyTemplate = (t) => {
    const merged = t.body
      .replaceAll("{name}", (name || "there").split(" ")[0])
      .replaceAll("{agent}", me?.name?.split(" ")[0] || "your acquisitions rep")
      .replaceAll("{address}", address || "your property");
    setSmsBody(merged);
  };

  const sendSms = async () => {
    if (!phone) return toast.error("No phone number");
    if (!smsBody.trim()) return toast.error("Type a message first");
    setSmsBusy(true);
    try {
      await api.post("/twilio/sms/send", {
        to: phone,
        body: smsBody.trim(),
        lead_id: leadId || undefined,
      });
      toast.success("Message sent");
      setSmsBody("");
      setTab("history");
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSmsBusy(false); }
  };

  const notConfigured = !config.configured;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-lg" data-testid="callsms-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall size={16} />
            {name ? `${name}` : "Call / SMS"}
            <span className="text-xs text-[var(--app-text-muted)] font-mono ml-2">
              {phone}
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Place a call, send an SMS, or view call and SMS history for this contact.
          </DialogDescription>
        </DialogHeader>

        {/* Native phone actions — always visible, no Twilio required */}
        {phone && (
          <div className="border border-[var(--app-line)] rounded-lg p-3 bg-[var(--app-surface)]/40 mb-2">
            <div className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mb-2 flex items-center gap-1">
              <Smartphone size={11} /> Use my phone
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setNativeDialog({ open: true, mode: "call" })}
                className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)] transition-colors text-sm font-medium"
                data-testid="native-call-btn"
              >
                <PhoneCall size={14} /> Call from my phone
              </button>
              <button
                onClick={() => setNativeDialog({ open: true, mode: "sms" })}
                className="inline-flex items-center justify-center gap-2 h-11 rounded-full border border-[var(--app-line)] hover:bg-white transition-colors text-sm font-medium"
                data-testid="native-sms-btn"
              >
                <MessageSquare size={14} /> Text from my phone
              </button>
            </div>
            <div className="text-[11px] text-[var(--app-text-muted)] mt-2">
              Opens a dialog with the number, a Copy button, and a QR code you
              can scan with your phone. Works on desktop & mobile.
            </div>
          </div>
        )}

        {notConfigured ? (
          <div className="text-xs text-[var(--app-text-muted)] border border-dashed border-[var(--app-line)] rounded-lg p-3">
            Cloud SMS is unavailable until an admin configures the SignalWire
            provider. Use the buttons above to call/text from your own phone.
          </div>
        ) : (
          <>
            <div className="flex gap-1 border-b border-[var(--app-line)] -mt-2 mb-2">
              {[
                { k: "call", label: "Call", icon: PhoneCall },
                { k: "sms", label: "SMS", icon: MessageSquare },
                { k: "history", label: `History (${history.length})` },
              ].map((t) => (
                <button
                  key={t.k}
                  onClick={() => setTab(t.k)}
                  data-testid={`tab-${t.k}`}
                  className={`px-3 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                    tab === t.k
                      ? "border-[var(--app-black)] text-[var(--app-text)]"
                      : "border-transparent text-[var(--app-text-muted)] hover:text-[var(--app-text)]"
                  }`}
                >
                  {t.icon && <t.icon size={12} className="inline mr-1.5" />}
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "call" && (
              <div className="space-y-3">
                <div className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)]">
                  Browser softphone (SignalWire · recorded)
                </div>
                <div className="text-xs text-[var(--app-text-muted)]">
                  Opens the built-in dialer and places the call from a
                  SignalWire number in your rotating caller-ID pool. Seller
                  callbacks route through your inbound Retell/AI flow.
                </div>
                <Button
                  onClick={placeCall}
                  disabled={!phone}
                  className="w-full rounded-full h-11 bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
                  data-testid="place-call-button"
                >
                  <PhoneCall className="mr-2" size={14} />
                  Call {phone}
                </Button>
              </div>
            )}

            {tab === "sms" && (
              <div className="space-y-3">
                <div className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)]">
                  Advanced: Twilio cloud SMS (requires A2P registration)
                </div>
                <div>
                  <Label className="text-xs">Templates</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {SMS_TEMPLATES.map((t) => (
                      <button
                        key={t.key}
                        onClick={() => applyTemplate(t)}
                        className="text-[10px] uppercase tracking-widest border border-[var(--app-line)] rounded-full px-2.5 py-1 hover:bg-[var(--app-surface)]"
                        data-testid={`tpl-${t.key}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Message</Label>
                  <Textarea
                    value={smsBody}
                    onChange={(e) => setSmsBody(e.target.value)}
                    rows={5}
                    className="mt-1"
                    placeholder="Type your message… (must include an opt-out like 'Reply STOP')"
                    data-testid="sms-body"
                  />
                  <div className="text-[11px] text-[var(--app-text-muted)] mt-1">
                    {smsBody.length} chars · Twilio splits at 160
                  </div>
                </div>
                <Button
                  onClick={sendSms}
                  disabled={smsBusy || !smsBody.trim() || !phone}
                  className="w-full rounded-full h-11 bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
                  data-testid="send-sms-button"
                >
                  {smsBusy ? (
                    <Loader2 className="animate-spin mr-2" size={14} />
                  ) : (
                    <MessageSquare className="mr-2" size={14} />
                  )}
                  Send SMS
                </Button>
              </div>
            )}

            {tab === "history" && (
              <div className="max-h-[50vh] overflow-y-auto" data-testid="history-list">
                {histLoading ? (
                  <div className="text-xs text-[var(--app-text-muted)] py-6 text-center">
                    <Loader2 className="animate-spin inline mr-2" size={14} />
                    Loading…
                  </div>
                ) : history.length === 0 ? (
                  <div className="text-xs text-[var(--app-text-muted)] py-6 text-center">
                    No calls or messages yet.
                  </div>
                ) : (
                  history.map((r) => <TimelineRow key={`${r.kind}-${r.id}`} row={r} />)
                )}
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="callsms-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
      <NativePhoneDialog
        open={nativeDialog.open}
        onOpenChange={(v) => setNativeDialog((s) => ({ ...s, open: v }))}
        phone={phone}
        name={name}
        mode={nativeDialog.mode}
        leadId={leadId}
      />
    </Dialog>
  );
}
